'use client'

import { useIsClient } from '@/core/hooks/use-is-client'
import { Button } from '@/core/ui/atoms/button'
import { Input } from '@/core/ui/forms/input'
import { getSendVerificationCodeValidation } from '@/features/auth/form-validation/send-verification-code.validation'
import { useSendVerificationCode } from '@/features/auth/ui/hooks/use-send-verification-code'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { FormProvider, useForm } from 'react-hook-form'

const FormPhoneInput = dynamic(
  () => import('@/core/ui/forms/phone-input').then(mod => mod.FormPhoneInput),
  { ssr: false, loading: () => <Input disabled /> },
)

type Props = {
  onClose?: () => void
  onSuccess?: () => void
}

export const SendVerificationCode: React.FC<Props> = ({
  onClose,
  onSuccess,
}) => {
  const t = useTranslations()
  const isClient = useIsClient()
  const { sendCode, isPending } = useSendVerificationCode()
  const { schema } = getSendVerificationCodeValidation(t)
  const form = useForm({
    defaultValues: { phoneNumber: '' },
    resolver: zodResolver(schema),
    disabled: !isClient,
  })

  const onSubmit = form.handleSubmit(async data => {
    try {
      await sendCode(data.phoneNumber)
      onSuccess?.()
    } catch {
      form.setError('phoneNumber', {
        message: t('notifications.sendVerificationCodeFailed'),
      })
    }
  })

  return (
    <div className='text-navy px-1'>
      <h2 className='my-4 text-xl font-medium'>{t('signin.title')}</h2>
      <p className='text-2xl font-semibold'>{t('signin.welcome')},</p>
      <p className='my-3'>{t('signin.subtitle')}</p>
      <FormProvider {...form}>
        <form className='flex flex-col items-stretch gap-3' onSubmit={onSubmit}>
          <label htmlFor='phoneNumber' className='text-sm font-medium'>
            {t('forms.phoneLabel')}
          </label>

          <FormPhoneInput
            name='phoneNumber'
            control={form.control}
            id='phoneNumber'
            className='w-full'
            placeholder={t('forms.phonePH')}
            disabled={isPending}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onSubmit(e)
              }
            }}
          />

          <div className='mt-5 flex justify-between gap-5'>
            <Button
              type='button'
              variant='outlined'
              onClick={onClose}
              disabled={isPending}
              className='min-w-36 flex-1'
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type='submit'
              color='navy'
              loading={isPending}
              className='min-w-36 flex-1'
            >
              {t('actions.sendCode')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
